import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Business,
  EmojiEvents,
  Groups,
  Handshake,
  Security,
  Star,
} from '@mui/icons-material';

const AboutPage: React.FC = () => {
  const leadership = [
    {
      name: 'Fernando McKenzie',
      role: 'Chief Executive Officer',
      bio: '10+ years in information technology.',
      image: 'FM',
    },
    {
      name: 'Honorine Ndom Ndzah',
      role: 'Chief Operating Officer',
      bio: '10+ years in operations management.',
      image: 'HN',

    },
  ];

  const values = [
    {
      title: 'Integrity',
      description: 'We maintain the highest ethical standards in all our business dealings.',
      icon: <Handshake />,
    },
    {
      title: 'Innovation',
      description: 'We leverage cutting-edge technology to solve complex challenges.',
      icon: <Star />,
    },
    {
      title: 'Excellence',
      description: 'We deliver exceptional quality in every project and interaction.',
      icon: <EmojiEvents />,
    },
    {
      title: 'Partnership',
      description: 'We build lasting relationships based on trust and mutual success.',
      icon: <Groups />,
    },
  ];

  const milestones = [
    { year: '2025', event: 'Company founded with focus on government IT services' },
  ];

  const certifications = [
    'IN-Progress',
    'ISO 9001:2015',
    'ISO 27001:2013',
    'CMMI Level 3',
    'SOC 2 Type II',
    'FedRAMP Ready',
    'NIST 800-171',
  ];

  // const partners = [
  //   'IN-Progress',
  //   'AWS Advanced',
  //   'Microsoft Cloud',
  //   'Google Cloud',
  // ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            About Intellusia Studios
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 800 }}>
            A trusted technology partner for government agencies and enterprises, 
            delivering innovative solutions that transform operations and drive mission success.
          </Typography>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h4" gutterBottom>
                  Our Mission
                </Typography>
                <Typography variant="body1">
                  To empower government agencies and enterprises with cutting-edge technology 
                  solutions that enhance operational efficiency, strengthen security posture, 
                  and deliver measurable value to stakeholders and citizens.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: 'secondary.main', color: 'white' }}>
                <Typography variant="h4" gutterBottom>
                  Our Vision
                </Typography>
                <Typography variant="body1">
                  To be the premier technology partner for government and enterprise clients, 
                  recognized for our innovation, integrity, and commitment to delivering 
                  transformative solutions that shape the future of public service.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Company Values */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Core Values
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {values.map((value) => (
              <Grid item xs={12} sm={6} md={3} key={value.title}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {React.cloneElement(value.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Leadership Team */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Leadership Team
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2, justifyContent: 'center' }}>
            {leadership.map((leader) => (
              <Grid item xs={12} sm={6} md={4} key={leader.name}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '2rem',
                      }}
                    >
                      {leader.image}
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      {leader.name}
                    </Typography>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      {leader.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {leader.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Company Timeline */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Journey
          </Typography>
          <Timeline position="alternate" sx={{ mt: 4 }}>
            {milestones.map((milestone, index) => (
              <TimelineItem key={milestone.year}>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  {index < milestones.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" color="primary">
                      {milestone.year}
                    </Typography>
                    <Typography variant="body2">
                      {milestone.event}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </Box>

      {/* Certifications & Partners */}
      {/* <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                Certifications
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {certifications.map((cert) => (
                  <Chip
                    key={cert}
                    label={cert}
                    icon={<Security />}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                Technology Partners
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {partners.map((partner) => (
                  <Chip
                    key={partner}
                    label={partner}
                    icon={<Business />}
                    color="primary"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box> */}

      {/* Stats */}
      {/* <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2">500+</Typography>
                <Typography variant="h6">Projects Delivered</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2">50+</Typography>
                <Typography variant="h6">Government Contracts</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2">200+</Typography>
                <Typography variant="h6">Team Members</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2">98%</Typography>
                <Typography variant="h6">Client Satisfaction</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box> */}
    </Box>
  );
};

export default AboutPage;